import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  DollarSign, 
  FileText, 
  Upload, 
  Calendar,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { DatabaseService } from '../lib/database';
import { useDeal } from '../contexts/DealContext';
import { useUser } from '@clerk/clerk-react';

interface DealOnboardingFormProps {
  onComplete: () => void;
  onCancel: () => void;
}

interface FinancialDataEntry {
  month: string;
  year: number;
  totalIncome: number;
  totalCOGS: number;
  totalExpense: number;
  netOrdinaryIncome: number;
}

export function DealOnboardingForm({ onComplete, onCancel }: DealOnboardingFormProps) {
  const { selectedDeal } = useDeal();
  const { user } = useUser();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form data
  const [businessInfo, setBusinessInfo] = useState({
    businessType: '',
    industry: '',
    yearsInBusiness: '',
    employeeCount: '',
    description: ''
  });
  
  const [financialData, setFinancialData] = useState<FinancialDataEntry[]>([]);
  const [dataEntryMethod, setDataEntryMethod] = useState<'manual' | 'upload'>('manual');
  const [isLoading, setIsLoading] = useState(true);

  // Load saved data when component mounts
  useEffect(() => {
    const loadSavedData = async () => {
      if (!selectedDeal) {
        setIsLoading(false);
        return;
      }

      try {
        // Get the deal data to check for saved business info
        const dealData = await DatabaseService.getDeal(selectedDeal.deal_id);
        if (dealData?.data) {
          // Load business info from new columns instead of metadata
          const businessInfoData = {
            industry: dealData.data.industry || '',
            description: dealData.data.business_description || '',
            businessType: dealData.data.business_type || '',
            employeeCount: dealData.data.employee_count || '',
            yearsInBusiness: dealData.data.years_in_business || ''
          };
          setBusinessInfo(businessInfoData);
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedData();
  }, [selectedDeal]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debouncedSave.current) {
        clearTimeout(debouncedSave.current);
      }
    };
  }, []);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  // Generate public business alias from industry and deal_id
  const generatePublicBusinessAlias = (industry: string, dealId: string) => {
    if (!industry || !dealId) return '';
    
    // Clean industry name: remove spaces, special chars, convert to uppercase
    const cleanIndustry = industry
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .substring(0, 10); // Limit to 10 chars
    
    // Get last 4 characters of deal_id
    const dealSuffix = dealId.slice(-4);
    
    return `${cleanIndustry}_${dealSuffix}`;
  };

  // Debounced save function
  const debouncedSave = React.useRef<NodeJS.Timeout | null>(null);
  
  const saveBusinessInfoToDatabase = async (businessInfoData: any) => {
    if (!selectedDeal) return;
    
    try {
      // Update deal with business information using new columns
      const result = await DatabaseService.updateDeal({
        id: selectedDeal.deal_id,
        industry: businessInfoData.industry,
        business_description: businessInfoData.description,
        business_type: businessInfoData.businessType,
        employee_count: businessInfoData.employeeCount,
        years_in_business: businessInfoData.yearsInBusiness
      }, user?.id);
      
      if (result.error) {
        console.error('UpdateDeal error:', result.error);
      }
    } catch (err) {
      console.error('Error saving business info to database:', err);
    }
  };

  // Debounced save function
  const debouncedSaveBusinessInfo = (businessInfoData: any) => {
    // Clear existing timeout
    if (debouncedSave.current) {
      clearTimeout(debouncedSave.current);
    }
    
    // Set new timeout
    debouncedSave.current = setTimeout(() => {
      saveBusinessInfoToDatabase(businessInfoData);
    }, 1000); // Save after 1 second of inactivity
  };

  // Handle field changes (update local state immediately, save debounced)
  const handleFieldChange = (field: string, value: string) => {
    const updatedBusinessInfo = { ...businessInfo, [field]: value };
    setBusinessInfo(updatedBusinessInfo);
    
    // Debounced save to database
    debouncedSaveBusinessInfo(updatedBusinessInfo);
  };

  // Handle field blur (save immediately when user leaves field)
  const handleFieldBlur = (field: string, value: string) => {
    // Clear any pending debounced save
    if (debouncedSave.current) {
      clearTimeout(debouncedSave.current);
    }
    
    // Save immediately
    const updatedBusinessInfo = { ...businessInfo, [field]: value };
    saveBusinessInfoToDatabase(updatedBusinessInfo);
  };

  const handleBusinessInfoSubmit = async () => {
    if (!businessInfo.businessType || !businessInfo.industry) {
      setError('Please fill in all required fields');
      return;
    }
    
    // Save business info immediately when user clicks Continue
    if (selectedDeal) {
      try {
        await DatabaseService.updateDeal({
          id: selectedDeal.deal_id,
          metadata: {
            businessInfo
          }
        }, user?.id);
      } catch (err) {
        console.error('Error saving business info:', err);
        setError('Failed to save business information');
        return;
      }
    }
    
    setStep(2);
    setError(null);
  };

  const handleFinancialDataSubmit = async () => {
    if (dataEntryMethod === 'manual' && financialData.length === 0) {
      setError('Please add at least one month of financial data');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Save business information to deal metadata
      if (selectedDeal) {
        await DatabaseService.updateDeal({
          id: selectedDeal.deal_id,
          metadata: {
            businessInfo
          }
        }, user?.id);
      }

      // Save financial data
      if (dataEntryMethod === 'manual' && financialData.length > 0) {
        for (const data of financialData) {
          const periodStart = new Date(data.year, months.indexOf(data.month));
          const periodEnd = new Date(data.year, months.indexOf(data.month) + 1, 0);
          
          // Save each financial metric
          const metrics = [
            { type: 'monthly_revenue', amount: data.totalIncome },
            { type: 'monthly_cogs', amount: data.totalCOGS },
            { type: 'monthly_expenses', amount: data.totalExpense },
            { type: 'monthly_net_income', amount: data.netOrdinaryIncome },
            { type: 'monthly_gross_profit', amount: data.totalIncome - data.totalCOGS }
          ];

          for (const metric of metrics) {
            await DatabaseService.createDealFinancialData({
              deal_id: selectedDeal!.deal_id,
              data_type: metric.type,
              period_start: periodStart.toISOString().split('T')[0],
              period_end: periodEnd.toISOString().split('T')[0],
              amount: metric.amount,
              currency: 'USD'
            });
          }
        }
      }

      onComplete();
    } catch (err) {
      console.error('Error saving deal data:', err);
      setError(err instanceof Error ? err.message : 'Failed to save data');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipFinancialData = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Save business information to deal columns only
      if (selectedDeal) {
        const result = await DatabaseService.updateDeal({
          id: selectedDeal.deal_id,
          industry: businessInfo.industry,
          business_description: businessInfo.description,
          business_type: businessInfo.businessType,
          employee_count: businessInfo.employeeCount,
          years_in_business: businessInfo.yearsInBusiness
        }, user?.id);
        if (result.error) {
          console.error('UpdateDeal error:', result.error);
        }
      }

      onComplete();
    } catch (err) {
      console.error('Error saving deal data:', err);
      setError(err instanceof Error ? err.message : 'Failed to save data');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addFinancialDataEntry = () => {
    setFinancialData([...financialData, {
      month: months[0],
      year: currentYear,
      totalIncome: 0,
      totalCOGS: 0,
      totalExpense: 0,
      netOrdinaryIncome: 0
    }]);
  };

  const updateFinancialData = (index: number, field: keyof FinancialDataEntry, value: string | number) => {
    const updated = [...financialData];
    updated[index] = { ...updated[index], [field]: value };
    setFinancialData(updated);
  };

  const removeFinancialDataEntry = (index: number) => {
    setFinancialData(financialData.filter((_, i) => i !== index));
  };

  // Show loading state while data is being loaded
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-4xl p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading saved data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-4xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-xl">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Deal Setup Required</h1>
          </div>
          <p className="text-blue-100">
            Complete the setup for <strong>{selectedDeal?.business_name}</strong> to access financial analysis tools.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-center space-x-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                1
              </div>
              <span className="text-sm font-medium">Business Info</span>
            </div>
            <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                2
              </div>
              <span className="text-sm font-medium">Financial Data</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Business Information</h2>
                <p className="text-gray-600 mb-6">Tell us about the business to help us provide better analysis.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Type *
                  </label>
                  <select
                    value={businessInfo.businessType}
                    onChange={(e) => handleFieldChange('businessType', e.target.value)}
                    onBlur={(e) => handleFieldBlur('businessType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select business type...</option>
                    <option value="service">Service Business</option>
                    <option value="retail">Retail Business</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="restaurant">Restaurant/Food Service</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="technology">Technology</option>
                    <option value="construction">Construction</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry *
                  </label>
                  <input
                    type="text"
                    value={businessInfo.industry}
                    onChange={(e) => handleFieldChange('industry', e.target.value)}
                    onBlur={(e) => handleFieldBlur('industry', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., HVAC Services, Retail, Manufacturing"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years in Business
                  </label>
                  <select
                    value={businessInfo.yearsInBusiness}
                    onChange={(e) => handleFieldChange('yearsInBusiness', e.target.value)}
                    onBlur={(e) => handleFieldBlur('yearsInBusiness', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select years...</option>
                    <option value="0-1">Less than 1 year</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10-20">10-20 years</option>
                    <option value="20+">20+ years</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Employees
                  </label>
                  <select
                    value={businessInfo.employeeCount}
                    onChange={(e) => handleFieldChange('employeeCount', e.target.value)}
                    onBlur={(e) => handleFieldBlur('employeeCount', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select employee count...</option>
                    <option value="1-5">1-5 employees</option>
                    <option value="6-10">6-10 employees</option>
                    <option value="11-25">11-25 employees</option>
                    <option value="26-50">26-50 employees</option>
                    <option value="51-100">51-100 employees</option>
                    <option value="100+">100+ employees</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Description
                </label>
                <textarea
                  value={businessInfo.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  onBlur={(e) => handleFieldBlur('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Brief description of the business operations..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBusinessInfoSubmit}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Financial Data</h2>
                <p className="text-gray-600 mb-6">Add financial data to enable analysis and reporting.</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-900 mb-1">Data Entry Options</h3>
                    <p className="text-sm text-blue-800">
                      You can add financial data manually or upload a CSV file. We recommend adding at least 12 months of data for accurate analysis.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <button
                    onClick={() => setDataEntryMethod('manual')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                      dataEntryMethod === 'manual'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    Manual Entry
                  </button>
                  <button
                    onClick={() => setDataEntryMethod('upload')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                      dataEntryMethod === 'upload'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Upload CSV
                  </button>
                </div>

                {dataEntryMethod === 'manual' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-medium text-gray-900">Monthly Financial Data</h3>
                      <button
                        onClick={addFinancialDataEntry}
                        className="flex items-center gap-2 px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <FileText className="w-4 h-4" />
                        Add Month
                      </button>
                    </div>

                    {financialData.map((entry, index) => (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium text-gray-900">Month {index + 1}</h4>
                          <button
                            onClick={() => removeFinancialDataEntry(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                            <select
                              value={entry.month}
                              onChange={(e) => updateFinancialData(index, 'month', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {months.map(month => (
                                <option key={month} value={month}>{month}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                            <select
                              value={entry.year}
                              onChange={(e) => updateFinancialData(index, 'year', parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Total Income</label>
                            <input
                              type="number"
                              value={entry.totalIncome}
                              onChange={(e) => updateFinancialData(index, 'totalIncome', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="0"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Total COGS</label>
                            <input
                              type="number"
                              value={entry.totalCOGS}
                              onChange={(e) => updateFinancialData(index, 'totalCOGS', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="0"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Total Expenses</label>
                            <input
                              type="number"
                              value={entry.totalExpense}
                              onChange={(e) => updateFinancialData(index, 'totalExpense', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="0"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Net Income</label>
                            <input
                              type="number"
                              value={entry.netOrdinaryIncome}
                              onChange={(e) => updateFinancialData(index, 'netOrdinaryIncome', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="0"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {dataEntryMethod === 'upload' && (
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Upload CSV File</h3>
                    <p className="text-gray-600 mb-4">
                      Upload a CSV file with your financial data. The file should include columns for:
                      Month, Year, Total Income, Total COGS, Total Expenses, Net Income.
                    </p>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Choose File
                    </button>
                    <p className="text-sm text-gray-500 mt-2">
                      CSV upload feature coming soon. Please use manual entry for now.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-between gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Back
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={onCancel}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSkipFinancialData}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        Skip for Now
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleFinancialDataSubmit}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Complete Setup
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
